import {
  Box,
  Button,
  ConversationList,
  HubLayout,
  Image,
  Spinner,
  Text,
  Title,
  useMediaQuery,
  useTheme,
  useToast,
} from '@color/continuum';
import { logAnalyticsPageView, navigateAndRefreshBrowser, trackAnalyticsEvent } from '@color/lib';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import Smooch from 'smooch';

import {
  useConversationUnreadStatusesQuery,
  useFetchAndProcessConversations,
  useSmoochGetConversationsQuery,
} from './apis/api';
import EmptyStateImage from './assets/emptyState.svg';
import HeaderImage from './assets/headerImage.png';
import { messages } from './messages';
import { PATIENT_MESSAGING_CONVERSATION_CREATE_PATH } from './paths';
import {
  IConversationWithMessageIds,
  IZendeskConversation,
  IZendeskMessage,
  MessageSenderRole,
} from './types';
import {
  convertZendeskMessageToInternalRepresentation,
  handleUpdateParticipantListWithNewMessage,
} from './utils';

export const PatientMessagingInbox: React.FC = () => {
  const history = useHistory();
  const { breakpoints } = useTheme();
  const isSmallScreen = useMediaQuery(breakpoints.down('sm'));
  const { showErrorToast } = useToast();
  const {
    data: fetchedConversations,
    isError,
    isFetching: areConversationsFetching,
  } = useSmoochGetConversationsQuery();
  const { data: conversationUnreadStatuses, isFetching: areConversationStatusesFetching } =
    useConversationUnreadStatusesQuery();
  const [conversationList, setConversationList] = useState<IConversationWithMessageIds[]>([]);
  const [hasSyncedConversationList, setHasSyncedConversationList] = useState(false);
  const {
    isError: encounteredErrorProcessingConversations,
    isFetching: isFetchAndProcessConversationsFetching,
  } = useFetchAndProcessConversations(
    fetchedConversations ?? [],
    conversationUnreadStatuses?.conversation_statuses ?? {},
    areConversationsFetching,
    areConversationStatusesFetching,
    {
      onSuccess: (data: IConversationWithMessageIds[] | undefined) => {
        setConversationList(data ?? []);
        setHasSyncedConversationList(true);
      },
    }
  );

  if (encounteredErrorProcessingConversations || isError) {
    showErrorToast("Error loading inbox. Please contact Color and we'll take a look!");
    navigateAndRefreshBrowser('/dashboard');
  }

  if (
    areConversationsFetching ||
    areConversationStatusesFetching ||
    isFetchAndProcessConversationsFetching ||
    !hasSyncedConversationList
  ) {
    return <Spinner />;
  }
  Smooch.on('message', (message: IZendeskMessage, data: { conversation: IZendeskConversation }) => {
    // If we haven't seen this message before, then we should mark the conversation as unread and
    // potentially update the last message for the conversation.
    const fetchedConversationIndex = conversationList?.findIndex(
      (conversation: IConversationWithMessageIds) => conversation.id === data.conversation.id
    );
    const fetchedConversation = conversationList?.[fetchedConversationIndex];

    if (fetchedConversation && !fetchedConversation.messageIds.has(message.id)) {
      setConversationList((prevConversationList: IConversationWithMessageIds[]) => {
        if (message.role === MessageSenderRole.BUSINESS) {
          prevConversationList[fetchedConversationIndex].isUnreadByPatient = true;
          prevConversationList[fetchedConversationIndex].participantNames =
            handleUpdateParticipantListWithNewMessage(
              prevConversationList[fetchedConversationIndex].participantNames,
              message.displayName
            );
        } else {
          prevConversationList[fetchedConversationIndex].isUnreadByPatient = false;
        }
        prevConversationList[fetchedConversationIndex].messageIds.add(message.id);
        if (new Date(message.received * 1000) > fetchedConversation.latestMessage.sentAt) {
          prevConversationList[fetchedConversationIndex].latestMessage =
            convertZendeskMessageToInternalRepresentation(message);
          return prevConversationList.sort(
            (a: IConversationWithMessageIds, b: IConversationWithMessageIds) =>
              new Date(b.latestMessage.sentAt).getTime() -
              new Date(a.latestMessage.sentAt).getTime()
          );
        }
        return prevConversationList;
      });
    }
  });

  logAnalyticsPageView('messaging - patient-dashboard - messages', {
    conversations: conversationList?.length,
  });

  return (
    <HubLayout
      helmetTitle="Messages"
      headerImage={HeaderImage}
      headerContent={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'start',
            justifyItems: 'start',
          }}
        >
          <Title variant="headline1">
            <FormattedMessage {...messages.headerTitle} />
          </Title>
          <Text>
            <FormattedMessage {...messages.headerBody} />
          </Text>
          <Button
            variant="color-primary"
            fullWidth={isSmallScreen}
            onClick={() => {
              trackAnalyticsEvent('messaging - patient-dashboard - new message button clicked');
              history.push(PATIENT_MESSAGING_CONVERSATION_CREATE_PATH);
            }}
          >
            <FormattedMessage {...messages.newMessage} />
          </Button>
        </Box>
      }
      bodyContent={<ConversationList conversationList={conversationList} />}
      isEmptyState={conversationList.length === 0}
      emptyStateContent={
        <Box
          sx={{
            justifySelf: 'center',
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            width: { xs: '100%', md: '50%' },
          }}
        >
          <Image src={EmptyStateImage} alt="" />
          <Title variant="headline1">
            <FormattedMessage {...messages.noMessagesTitle} />
          </Title>
          <Text
            variant="body1"
            sx={{
              textAlign: 'center',
            }}
          >
            <FormattedMessage {...messages.noMessagesBody} />
          </Text>
          <Button
            variant="color-primary"
            fullWidth={isSmallScreen}
            onClick={() => {
              trackAnalyticsEvent('messaging - patient-dashboard - new message button clicked');
              history.push(PATIENT_MESSAGING_CONVERSATION_CREATE_PATH);
            }}
          >
            <FormattedMessage {...messages.newMessage} />
          </Button>
        </Box>
      }
    />
  );
};
